// Import Dependencies
import { Navigate } from "react-router";

// Local Imports
import { DynamicLayout } from "@layout/DynamicLayout";
import { AppLayout } from "@layout/AppLayout";
import AuthGuard from "app/presentation/components/guards/AuthGuard";

// ----------------------------------------------------------------------
const appMode = import.meta.env.VITE_APP_MODE || import.meta.env.MODE;

// Helper: Routes communes à tous les modes (Profil uniquement)
const getCommonRoutes = () => [
  {
    path: "profile",
    lazy: async () => ({
      Component: (await import("app/presentation/features/shared/profile")).default,
    }),
  },
];

// Helper: Routes Settings
const getSettingsRoutes = () => ({
  path: "settings",
  lazy: async () => ({
    Component: (await import("app/presentation/features/shared/settings/Layout")).default,
  }),
  children: [
    { index: true, element: <Navigate to="/settings/general" /> },
    { path: "general", lazy: async () => ({ Component: (await import("app/presentation/features/shared/settings/sections/General")).default }) },
    { path: "appearance", lazy: async () => ({ Component: (await import("app/presentation/features/shared/settings/sections/Appearance")).default }) },
    { path: "notifications", lazy: async () => ({ Component: (await import("app/presentation/features/shared/settings/sections/Notifications")).default }) },
    { path: "applications", lazy: async () => ({ Component: (await import("app/presentation/features/shared/settings/sections/Applications")).default }) },
    { path: "sessions", lazy: async () => ({ Component: (await import("app/presentation/features/shared/settings/sections/Sessions")).default }) },
    { path: "billing", lazy: async () => ({ Component: (await import("app/presentation/features/shared/settings/sections/Billing")).default }) },
  ],
});

// ----------------------------------------------------------------------

// Routes pour le mode chauffeur
const driverRoutes = (appMode === "driver") ? {
  id: "protected",
  Component: AuthGuard,
  children: [
    {
      Component: DynamicLayout,
      children: [
        { index: true, element: <Navigate to="/driver/activity" replace /> },
        { path: "/driver/dashboard", element: <Navigate to="/driver/activity" replace /> },
        { path: "/driver/activity", lazy: async () => ({ Component: (await import("app/presentation/features/driver/activity")).default }) },
        { path: "/driver/shift", lazy: async () => ({ Component: (await import("app/presentation/features/driver/activity")).default }) },
        { path: "/driver/courses", lazy: async () => ({ Component: (await import("app/presentation/features/driver/activity")).default }) },
        { path: "/driver/finances", lazy: async () => ({ Component: (await import("app/presentation/features/driver/finance")).default }) },
        ...getCommonRoutes(),
        { path: "*", element: <Navigate to="/driver/activity" replace /> },
      ],
    },
    { Component: AppLayout, children: [getSettingsRoutes()] },
  ],
} : { id: "driver-placeholder" };

// Routes pour le mode entreprise
const entrepriseRoutes = (appMode === "entreprise") ? {
  id: "protected",
  Component: AuthGuard,
  children: [
    {
      Component: DynamicLayout,
      children: [
        { index: true, element: <Navigate to="/entreprise/dashboard" replace /> },
        { path: "entreprise/dashboard", lazy: async () => ({ Component: (await import("app/presentation/features/entreprise/pages/DashboardPage")).default }) },
        { path: "rides", lazy: async () => ({ Component: (await import("app/presentation/features/entreprise/pages/RidesPage")).default }) },
        { path: "drivers", lazy: async () => ({ Component: (await import("app/presentation/features/entreprise/pages/DriversPage")).default }) },
        { path: "vehicles", lazy: async () => ({ Component: (await import("app/presentation/features/entreprise/pages/VehiclesPage")).default }) },
        { path: "analytics", lazy: async () => ({ Component: (await import("app/presentation/features/entreprise/pages/AnalyticsPage")).default }) },
        getSettingsRoutes(),
        { path: "kyc", lazy: async () => ({ Component: (await import("app/presentation/features/shared/kyc")).default }) },
        ...getCommonRoutes(),
        { path: "*", element: <Navigate to="/entreprise/dashboard" replace /> },
      ],
    },
  ],
} : { id: "entreprise-placeholder" };

// Routes pour le mode admin
const adminRoutes = (appMode === "admin" || appMode === "admin-limited") ? {
  id: "protected",
  Component: AuthGuard,
  children: [
    {
      Component: DynamicLayout,
      children: [
        { index: true, element: <Navigate to="/admin/overview" replace /> },
        { path: "/admin/overview", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/OverviewPage")).default }) },
        { path: "/admin/overview/in-live", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/InLivePage")).default }) },
        { path: "/admin/overview/tasks", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/TasksPage")).default }) },
        { path: "/admin/feuilles-route/:id/detail", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/FeuilleRouteDetailPage")).default }) },
        { path: "/admin/management/drivers", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/DriversPage")).default }) },
        { path: "/admin/management/vehicles", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/VehiclesPage")).default }) },
        { path: "/admin/management/users", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/UsersPage")).default }) },
        { path: "/admin/management/account-requests", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/AccountRequestsPage")).default }) },
        { path: "/admin/management/correction-requests", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/CorrectionRequestsPage")).default }) },
        { path: "/admin/management/verification", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/VerificationPage")).default }) },
        { path: "/admin/management/encoding", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/EncodingPage")).default }) },
        { path: "/admin/crm/clients", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/ClientsPage")).default }) },
        { path: "/admin/crm/partners", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/PartnersPage")).default }) },
        { path: "/admin/finance/performance", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/PerformancePage")).default }) },
        { path: "/admin/system/settings", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/SettingsPage")).default }) },
        { path: "/admin/system/queries", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/QueriesPage")).default }) },
        { path: "/admin/system/notifications", lazy: async () => ({ Component: (await import("app/presentation/features/admin/pages/NotificationsPage")).default }) },
        { path: "/admin", element: <Navigate to="/admin/overview" replace /> },
        ...getCommonRoutes(),
        { path: "*", element: <Navigate to="/admin/overview" replace /> },
      ],
    },
    { Component: AppLayout, children: [getSettingsRoutes()] },
  ],
} : { id: "admin-placeholder" };

// Sélection des routes selon le mode
export const protectedRoutes = (() => {
  if (appMode === "driver") return driverRoutes;
  if (appMode === "entreprise") return entrepriseRoutes;
  if (appMode === "admin" || appMode === "admin-limited") return adminRoutes;
  return driverRoutes;
})();
